import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import {
  IconPresentationProvider,
  ToolButton,
  useViewportGrid,
} from '@ohif/ui-next';

/** The split button that holds "additional tools". */
export const MORE_TOOLS_ID = 'MoreTools';

/** The scrollable band the toolbar sits in — the clipping viewport. */
const BAND_SELECTOR = '.gleamer-header-content';

type ToolbarProps = {
  servicesManager: unknown;
  /** Render only these ids (used to pin MoreTools outside the scroll band). */
  only?: string[];
  /** Render everything except these ids. */
  exclude?: string[];
  /**
   * When set, ids scrolled out of view are appended to the MoreTools section so
   * they remain reachable from its dropdown.
   */
  manageOverflow?: boolean;
};

export default function Toolbar({
  servicesManager,
  only,
  exclude,
  manageOverflow = false,
}: ToolbarProps): React.ReactElement {
  const { toolbarService } = (servicesManager as any).services;

  const [viewportGrid, viewportGridService] = useViewportGrid();

  const [toolbarButtons, setToolbarButtons] = useState([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<string, HTMLDivElement>());
  const appliedSection = useRef('');

  useEffect(() => {
    const updateToolbar = () => {
      // Modes register their toolbar under the 'primary' button section (the OHIF
      // 3.10 convention). Prefer a section keyed on the active viewport's
      // toolGroupId when one exists, but fall back to 'primary' — the mammo
      // hanging protocol uses toolGroupId 'default', which has no button section,
      // so the toolbar came up empty.
      const toolGroupId =
        viewportGridService.getActiveViewportOptionByKey('toolGroupId') ??
        'primary';
      const section = toolbarService.getButtonSection(toolGroupId);
      setToolbarButtons(
        section.length ? section : toolbarService.getButtonSection('primary')
      );
    };

    const { unsubscribe } = toolbarService.subscribe(
      toolbarService.EVENTS.TOOL_BAR_MODIFIED,
      updateToolbar
    );

    updateToolbar();

    return () => {
      unsubscribe();
    };
  }, [toolbarService, viewportGrid, viewportGridService]);

  useLayoutEffect(() => {
    if (!manageOverflow) {
      return;
    }
    const container = containerRef.current;
    const band = container?.closest(BAND_SELECTOR) as HTMLElement | null;
    if (!container || !band || typeof ResizeObserver === 'undefined') {
      return;
    }

    const compute = () => {
      // Resolve the band on every pass rather than closing over the one found
      // at setup: a stale node reports a zero rect, and every item would then
      // test as out of view.
      const view = (
        containerRef.current?.closest(BAND_SELECTOR) as HTMLElement | null
      )?.getBoundingClientRect();
      if (!view || view.width === 0) {
        return;
      }

      // Every tool stays rendered in the row — nothing is ever unmounted. That
      // is what makes this stable: an element that is never removed can always
      // be measured, and the row's own layout never changes as a result of what
      // we decide here. MoreTools lives outside this band, so editing its
      // section cannot feed back into these measurements either.
      const hidden: string[] = [];
      toolbarButtons.forEach(({ id }) => {
        const el = itemRefs.current.get(id);
        if (!el) {
          return;
        }
        const r = el.getBoundingClientRect();
        if (r.width === 0) {
          return;
        }
        if (r.right > view.right + 1 || r.left < view.left - 1) {
          hidden.push(id);
        }
      });

      // Derive the section's own entries every pass rather than snapshotting
      // them once: anything currently in MoreTools that also lives in this row
      // was put there by a previous pass, so excluding row ids recovers the
      // mode's original list. Snapshotting into a ref looks equivalent until the
      // toolbar remounts — the snapshot then captures an already-injected list,
      // the tool is treated as original, and it can never be removed again.
      const rowIds = new Set(toolbarButtons.map(({ id }) => id));
      const own = (toolbarService.getButtonSection(MORE_TOOLS_ID) || [])
        .map((b: { id: string }) => b.id)
        .filter((id: string) => !rowIds.has(id));

      const ids = [...own, ...hidden];
      const key = ids.join('|');
      if (appliedSection.current === key) {
        return;
      }
      appliedSection.current = key;

      // `createButtonSection` is deprecated in 3.13 and forwards to
      // `updateSection`, which APPENDS: it pushes the ids that aren't already
      // present and drops nothing. Calling it alone can only ever grow the
      // section — tools were pushed in as the header narrowed and stayed there
      // forever, which is precisely the reported bug. Clear first so the call
      // is a genuine replace.
      toolbarService.clearButtonSection(MORE_TOOLS_ID);
      toolbarService.updateSection(MORE_TOOLS_ID, ids);
    };

    compute();

    // The observer catches every way the band can change size that `resize`
    // cannot see — a side panel collapsing, a layout switch — while `resize`
    // covers the case the observer can miss: its callbacks ride the rendering
    // lifecycle, so they are not delivered while the tab is not being rendered.
    // `scroll` covers tools moving out of view with no size change at all.
    const observer = new ResizeObserver(compute);
    observer.observe(band);
    band.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);

    // Recompute as the MoreTools button is pressed, before its menu renders.
    // Both triggers above are *notifications* that the layout changed; this one
    // reads the geometry at the only moment the list is actually consumed, so a
    // missed notification cannot leave the menu offering a tool that is plainly
    // visible in the row.
    const onPointerDown = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.(`[data-cy^="${MORE_TOOLS_ID}"]`)) {
        compute();
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);

    return () => {
      observer.disconnect();
      band.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [manageOverflow, toolbarButtons, toolbarService]);

  const onInteraction = useCallback(
    args => toolbarService.recordInteraction(args),
    [toolbarService]
  );

  const visible = toolbarButtons.filter(({ id }) => {
    if (only) {
      return only.includes(id);
    }
    if (exclude) {
      return !exclude.includes(id);
    }
    return true;
  });

  return (
    // OHIF 3.11's `ohif.toolButton` renders through `ToolButtonWrapper`, which
    // only draws a properly sized, tooltipped, active-aware button when an
    // IconPresentationProvider supplies `IconContainer`. Without it, it silently
    // falls back to a bare `<Button variant="ghost" size="icon">`: tiny icon, no
    // tooltip and no click handler. Upstream wraps every toolbar in this provider
    // (see ui-next Header and OHIFViewportActionCorners) — so must we.
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <div ref={containerRef} className="flex items-center">
        {visible.map(toolDef => {
          const { id, Component, componentProps } = toolDef;
          return (
            // The margin for separating the tools on the toolbar should go here and NOT in each individual component (button) item.
            // This allows for the individual items to be included in other UI components where perhaps alternative margins are desired.
            <div
              key={id}
              ref={el => {
                if (el) {
                  itemRefs.current.set(id, el);
                } else {
                  itemRefs.current.delete(id);
                }
              }}
              className={classnames('mr-1')}
            >
              <Component
                id={id}
                {...componentProps}
                onInteraction={onInteraction}
                servicesManager={servicesManager}
              />
            </div>
          );
        })}
      </div>
    </IconPresentationProvider>
  );
}
